import React, { useState, useEffect, useRef } from 'react';
import { X, ArrowRight, ArrowLeft, CheckCircle, Building2, Video, Shield, Server, Wifi, AlertCircle, Map as MapIcon, Camera, Trash2, Settings, Volume2, Router, FileSpreadsheet } from 'lucide-react';
import axios from 'axios';
import LocationPickerModal from './LocationPickerModal';
import DeviceSelector from './DeviceSelector';
import DeviceInventoryCard from './DeviceInventoryCard';
import DeviceConfigModal from './DeviceConfigModal';
import { showSuccess, showError } from '../utils/notifications';
import { parseMassiveCamerasExcel, MassiveCameraRow } from '../utils/parseMassiveCamerasExcel';
import { provisionXtamMasivosSite } from '../utils/provisionXtamMasivosSite';
import './CreateSiteWizard.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3009/api/v1';

type SiteType = 'PVVC' | 'PRIVADO' | 'EU' | 'XTAM MASIVOS';
type DeviceType = 'cameras' | 'xtamVideos' | 'miniots' | 'routers' | 'buttons' | 'sirens';

/** client_services_id: 1 PRIVADO, 2 EU, 3 PVVC, 4 XTAM MASIVOS */
const SITE_SERVICE_XTAM_MASIVOS = 4;
const MAX_CAMERAS_PRIVADO_EU = 4;
const MAX_CAMERAS_MASIVOS = 500;

interface SiteFormData {
  clientServicesId: number; // 1 PRIVADO, 2 EU, 3 PVVC, 4 XTAM MASIVOS
  name: string;
  address: string;
  neighborhood: string;
  location: string;
  /** Departamento administrativo — certificado PDF (no es estado operativo del sitio) */
  geoDepartment: string;
  geoMunicipality: string;
  ipAddress: string;
  /** Solo XTAM MASIVOS: IP del servidor de grabaciones. */
  recordingsIp: string;
  latitude: string;
  longitude: string;
  active: boolean;
  certificate: boolean;
  /** PRIVADO/EU: si true, aplica corte por tiempo en vivo (configuración global en .env del frontend). */
  streamingOnDemandEnabled: boolean;
  numCameras: number; // PRIVADO/EU: 1-4; MASIVOS: cantidad del Excel
  selectedCameras: number[];
  selectedXtamVideos: number[];
  selectedMiniots: number[];
  selectedButtons: number[];
  selectedSirens: number[];
  selectedRouters: number[];
}

interface InventoryItem {
  id: number;
  serialDevice?: string;
  serial?: string;
  name?: string;
  isActive: boolean;
  modelName?: string;
  brandName?: string;
  [key: string]: any;
}

interface CreateSiteWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  token: string;
  editingSite?: {
    id: number;
    type: SiteType;
    name: string;
    address: string;
    neighborhood?: string;
    location?: string;
    geoDepartment?: string;
    geoMunicipality?: string;
    ipAddress: string;
    recordingsIp?: string;
    latitude?: string;
    longitude?: string;
    active: boolean;
    certificate?: boolean;
    streamingOnDemandEnabled?: boolean;
  } | null;
}

const CreateSiteWizard: React.FC<CreateSiteWizardProps> = ({ isOpen, onClose, onSuccess, token, editingSite }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<SiteFormData>({
    clientServicesId: 1, // 1: PRIVADO por defecto
    name: '',
    address: '',
    neighborhood: '',
    location: '',
    geoDepartment: '',
    geoMunicipality: '',
    ipAddress: '',
    recordingsIp: '',
    latitude: '',
    longitude: '',
    active: true,
    certificate: false,
    streamingOnDemandEnabled: true,
    numCameras: 0,
    selectedCameras: [],
    selectedXtamVideos: [],
    selectedMiniots: [],
    selectedButtons: [],
    selectedSirens: [],
    selectedRouters: [],
  });

  const [massiveCameras, setMassiveCameras] = useState<MassiveCameraRow[]>([]);
  const [massiveExcelName, setMassiveExcelName] = useState<string>('');
  const [massiveExcelError, setMassiveExcelError] = useState<string | null>(null);
  const massiveFileRef = useRef<HTMLInputElement | null>(null);
  const [inventoryCameras, setInventoryCameras] = useState<InventoryItem[]>([]);
  const [inventoryXtamVideos, setInventoryXtamVideos] = useState<InventoryItem[]>([]);
  const [inventoryMiniots, setInventoryMiniots] = useState<InventoryItem[]>([]);
  const [inventoryButtons, setInventoryButtons] = useState<InventoryItem[]>([]);
  const [inventorySirens, setInventorySirens] = useState<InventoryItem[]>([]);
  const [inventoryRouters, setInventoryRouters] = useState<InventoryItem[]>([]);
  // Bocinas del sitio ya persistidas (devices_sirens.id): habilita el check "tiene bocina" en cámaras.
  const [siteSirenDevices, setSiteSirenDevices] = useState<Array<{ id: number; ipaddress?: string | null }>>([]);

  const [siteHasExistingCameras, setSiteHasExistingCameras] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  
  // Nuevo estado para el flujo de selección de dispositivos
  const [selectedDeviceType, setSelectedDeviceType] = useState<DeviceType | null>(null);
  const [showDeviceSelector, setShowDeviceSelector] = useState(false);
  
  // Estado para modal de creación de dispositivo
  const [showCreateDeviceModal, setShowCreateDeviceModal] = useState(false);
  
  // Estado para modal de confirmación de reasignación
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [deviceToReassign, setDeviceToReassign] = useState<{
    deviceId: number;
    fromSiteId: number;
    fromSiteName: string;
  } | null>(null);

  // Estado para errores por campo
  const [fieldErrors, setFieldErrors] = useState<{[key: string]: string}>({});
  
  // Estado para mostrar validación en tiempo real
  const [touched, setTouched] = useState<{[key: string]: boolean}>({});
  
  // Estado para configuraciones de dispositivos
  const [deviceConfigurations, setDeviceConfigurations] = useState<{
    [key: string]: any; // key será "camera_1", "miniot_2", etc.
  }>({});

  // Keys de configuraciones que el usuario modificó explícitamente en el modal.
  // Se usa para decidir, en edición, qué dispositivos actualizar vs. dejar intactos.
  const [dirtyConfigKeys, setDirtyConfigKeys] = useState<Set<string>>(new Set());
  
  // Estado para modal de configuración de dispositivo
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [currentConfigDevice, setCurrentConfigDevice] = useState<{
    type: DeviceType;
    id: number;
    info: any;
  } | null>(null);
  
  // Estado para saber si la configuración es obligatoria (nuevo dispositivo)
  const [isConfigRequired, setIsConfigRequired] = useState(false);

  const authHeaders = { Authorization: `Bearer ${token}` };
  const isXtamMasivos = formData.clientServicesId === SITE_SERVICE_XTAM_MASIVOS;
  const MAX_CAMERAS = isXtamMasivos ? MAX_CAMERAS_MASIVOS : MAX_CAMERAS_PRIVADO_EU;
  const isPrivadoEuOrMasivos =
    formData.clientServicesId === 1 ||
    formData.clientServicesId === 2 ||
    isXtamMasivos;

  useEffect(() => {
    if (isOpen) {
      // Reiniciar seguimiento de configs modificadas cada vez que se abre el wizard.
      setDirtyConfigKeys(new Set());
      loadInventories();

      // Si estamos editando, cargar los datos del sitio
      if (editingSite) {
        // Mapear el tipo string a clientServicesId
        let clientServicesId = 1; // PRIVADO por defecto
        if (editingSite.type === 'EU') clientServicesId = 2;
        else if (editingSite.type === 'PVVC') clientServicesId = 3;
        else if (editingSite.type === 'XTAM MASIVOS' || String(editingSite.type).toUpperCase().includes('MASIV')) clientServicesId = SITE_SERVICE_XTAM_MASIVOS;
        
        setFormData({
          clientServicesId: clientServicesId,
          name: editingSite.name || '',
          address: editingSite.address || '',
          neighborhood: editingSite.neighborhood || '',
          location: editingSite.location || '',
          geoDepartment: editingSite.geoDepartment || '',
          geoMunicipality: editingSite.geoMunicipality || '',
          ipAddress: editingSite.ipAddress || '',
          recordingsIp: editingSite.recordingsIp || '',
          latitude: editingSite.latitude || '',
          longitude: editingSite.longitude || '',
          active: editingSite.active !== undefined ? editingSite.active : true,
          certificate: editingSite.certificate || false,
          streamingOnDemandEnabled: editingSite.streamingOnDemandEnabled !== false,
          numCameras: 0,
          selectedCameras: [],
          selectedXtamVideos: [],
          selectedMiniots: [],
          selectedButtons: [],
          selectedSirens: [],
          selectedRouters: [],
        });
        setMassiveCameras([]);
        setMassiveExcelName('');
        setMassiveExcelError(null);
        // Cargar dispositivos asignados al sitio
        loadSiteDevices(editingSite.id);
      }
    }
  }, [isOpen, editingSite]);

  // Función para cargar dispositivos asignados a un sitio
  const loadSiteDevices = async (siteId: number) => {
    try {
      // Cargar dispositivos de cada tipo
      const [cameras, xtamVideos, miniots, routers, buttons, sirens] = await Promise.all([
        axios.get(`${API_URL}/sites/${siteId}/cameras`, { headers: authHeaders }).catch(() => ({ data: { data: [] } })),
        axios.get(`${API_URL}/sites/${siteId}/xtam-videos`, { headers: authHeaders }).catch(() => ({ data: { data: [] } })),
        axios.get(`${API_URL}/sites/${siteId}/miniots`, { headers: authHeaders }).catch(() => ({ data: { data: [] } })),
        axios.get(`${API_URL}/sites/${siteId}/routers`, { headers: authHeaders }).catch(() => ({ data: { data: [] } })),
        axios.get(`${API_URL}/sites/${siteId}/buttons`, { headers: authHeaders }).catch(() => ({ data: { data: [] } })),
        axios.get(`${API_URL}/sites/${siteId}/sirens`, { headers: authHeaders }).catch(() => ({ data: { data: [] } })),
      ]);

      // Extraer los IDs de inventario de cada dispositivo
      const cameraIds = (cameras.data?.data || [])
        .map((d: any) => d.inventoryCamerasId)
        .filter((id: any) => id != null);
      
      const xtamVideoIds = (xtamVideos.data?.data || [])
        .map((d: any) => d.inventoryXtamVideosId)
        .filter((id: any) => id != null);
      
      const miniotIds = (miniots.data?.data || [])
        .map((d: any) => d.inventoryMiniotsId)
        .filter((id: any) => id != null);
      
      const routerIds = (routers.data?.data || [])
        .map((d: any) => d.inventoryRoutersId)
        .filter((id: any) => id != null);
      
      const buttonIds = (buttons.data?.data || [])
        .map((d: any) => d.inventoryButtonsId)
        .filter((id: any) => id != null);
      
      const sirenIds = (sirens.data?.data || [])
        .map((d: any) => d.inventorySirensId)
        .filter((id: any) => id != null);

      // Número de cámaras para PRIVADO/EU: usar cuántas tiene el sitio (1-4)
      const cameraCount = Math.min(Math.max(cameraIds.length, 0), MAX_CAMERAS);

      if (cameraIds.length > 0) {
        setSiteHasExistingCameras(true);
      }

      // Actualizar formData con los dispositivos asignados
      setFormData(prev => ({
        ...prev,
        numCameras: cameraCount,
        selectedCameras: cameraIds,
        selectedXtamVideos: xtamVideoIds,
        selectedMiniots: miniotIds,
        selectedRouters: routerIds,
        selectedButtons: buttonIds,
        selectedSirens: sirenIds,
      }));

      // También cargar las configuraciones de cada dispositivo
      const configs: {[key: string]: any} = {};
      
      // Cargar configs de cámaras
      (cameras.data?.data || []).forEach((camera: any) => {
        if (camera.inventoryCamerasId) {
          const configKey = `cameras_${camera.inventoryCamerasId}`;
          configs[configKey] = {
            name: camera.name,
            siteAddress: camera.siteAddress || '',
            ipAddress: camera.ipAddress || '',
            urlRtsp: camera.urlRtsp,
            isOnvif: camera.isOnvif,
            onvifUrl: camera.onvifUrl,
            onvifUsername: camera.onvifUsername,
            onvifPassword: '', // No cargar la contraseña por seguridad
            latitude: camera.latitude != null ? String(camera.latitude) : '',
            longitude: camera.longitude != null ? String(camera.longitude) : '',
            xtamVideoChannel: camera.xtamVideoChannel,
            isFromDvr: camera.isFromDvr,
            isAllowTesting: camera.isAllowTesting,
            isAllowAnalytics: camera.isAllowAnalytics,
            vmsId: camera.vmsId,
            sirenDeviceId: camera.sirenDeviceId ?? null,
          };
        }
      });

      // Cargar configs de XTAM Videos
      (xtamVideos.data?.data || []).forEach((xtam: any) => {
        if (xtam.inventoryXtamVideosId) {
          const configKey = `xtamVideos_${xtam.inventoryXtamVideosId}`;
          configs[configKey] = {
            ipAddress: xtam.ipAddress || null,
            state: xtam.state,
          };
        }
      });

      // Cargar configs de Routers
      (routers.data?.data || []).forEach((router: any) => {
        if (router.inventoryRoutersId) {
          const configKey = `routers_${router.inventoryRoutersId}`;
          configs[configKey] = {
            ipAddress: router.ipaddress,
          };
        }
      });

      // Cargar configs de Buttons
      (buttons.data?.data || []).forEach((button: any) => {
        if (button.inventoryButtonsId) {
          const configKey = `buttons_${button.inventoryButtonsId}`;
          configs[configKey] = {
            identificationButton: button.identificationButton,
            emailRegister: button.emailRegister,
            cellphoneRegister: button.cellphoneRegister,
            idSubscriber: button.idSubscriber,
            role: button.role,
            dataAuthorization: button.dataAuthorization,
          };
        }
      });

      // Guardar las bocinas del sitio (con su device id) para el check "tiene bocina" en cámaras.
      setSiteSirenDevices(
        (sirens.data?.data || []).map((s: any) => ({ id: s.id, ipaddress: s.ipaddress ?? null }))
      );

      // Cargar configs de Sirens
      (sirens.data?.data || []).forEach((siren: any) => {
        if (siren.inventorySirensId) {
          const configKey = `sirens_${siren.inventorySirensId}`;
          configs[configKey] = {
            ipAddress: siren.ipaddress,
          };
        }
      });

      setDeviceConfigurations(configs);
    } catch (e) {
      console.error('Error loading site devices:', e);
    }
  };

  const loadInventories = async () => {
    try {
      const [cameras, xtamVideos, miniots, buttons, sirens, routers] = await Promise.all([
        axios.get(`${API_URL}/inventory/cameras`, { headers: authHeaders, params: { isActive: true } }).catch(() => ({ data: { data: [] } })),
        axios.get(`${API_URL}/inventory/xtam-videos`, { headers: authHeaders, params: { isActive: true } }).catch(() => ({ data: { data: [] } })),
        axios.get(`${API_URL}/inventory/miniots`, { headers: authHeaders, params: { isActive: true } }).catch(() => ({ data: { data: [] } })),
        axios.get(`${API_URL}/inventory/buttons`, { headers: authHeaders, params: { isActive: true } }).catch(() => ({ data: { data: [] } })),
        axios.get(`${API_URL}/inventory/sirens`, { headers: authHeaders, params: { isActive: true } }).catch(() => ({ data: { data: [] } })),
        axios.get(`${API_URL}/inventory/routers`, { headers: authHeaders, params: { isActive: true } }).catch(() => ({ data: { data: [] } })),
      ]);

      setInventoryCameras(cameras.data?.data || []);
      setInventoryXtamVideos(xtamVideos.data?.data || []);
      setInventoryMiniots(miniots.data?.data || []);
      setInventoryButtons(buttons.data?.data || []);
      setInventorySirens(sirens.data?.data || []);
      setInventoryRouters(routers.data?.data || []);
    } catch (e) {
      console.error('Error loading inventories:', e);
    }
  };

  const refreshInventoryByType = async (deviceType: DeviceType) => {
    try {
      const endpointMap: Record<DeviceType, { url: string; setter: (data: any[]) => void }> = {
        cameras: { url: '/inventory/cameras', setter: setInventoryCameras },
        xtamVideos: { url: '/inventory/xtam-videos', setter: setInventoryXtamVideos },
        miniots: { url: '/inventory/miniots', setter: setInventoryMiniots },
        buttons: { url: '/inventory/buttons', setter: setInventoryButtons },
        sirens: { url: '/inventory/sirens', setter: setInventorySirens },
        routers: { url: '/inventory/routers', setter: setInventoryRouters },
      };
      const config = endpointMap[deviceType];
      const response = await axios.get(`${API_URL}${config.url}`, { headers: authHeaders, params: { isActive: true } });
      config.setter(response.data?.data || []);
    } catch (e) {
      console.error(`Error refreshing inventory ${deviceType}:`, e);
    }
  };

  // Validación de IP
  const validateIP = (ip: string): boolean => {
    if (!ip.trim()) return false;
    const ipPattern = /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    return ipPattern.test(ip);
  };

  // Validación del paso 1
  const validateStep1 = (): boolean => {
    const errors: {[key: string]: string} = {};
    
    if (!formData.clientServicesId || ![1, 2, 3, 4].includes(formData.clientServicesId)) {
      errors.clientServicesId = 'Debe seleccionar un tipo de sitio válido';
    }

    // PRIVADO y EU deben tener número de cámaras seleccionado (1-4); MASIVOS usa Excel en paso 2
    const isPrivadoOrEu = formData.clientServicesId === 1 || formData.clientServicesId === 2;
    if (isPrivadoOrEu && (formData.numCameras < 1 || formData.numCameras > 4)) {
      errors.numCameras = 'Seleccione el número de cámaras (1 a 4)';
    }

    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setError('Complete los datos requeridos antes de continuar.');
    }
    return Object.keys(errors).length === 0;
  };

  // Validación del paso 2
  const validateStep2 = (): boolean => {
    const errors: {[key: string]: string} = {};

      if (!formData.name.trim()) {
      errors.name = 'El nombre del sitio es requerido';
    } else if (formData.name.trim().length < 3) {
      errors.name = 'El nombre debe tener al menos 3 caracteres';
    } else if (formData.name.trim().length > 100) {
      errors.name = 'El nombre no puede exceder 100 caracteres';
    }

      if (!formData.address.trim()) {
      errors.address = 'La dirección es requerida';
    } else if (formData.address.trim().length < 5) {
      errors.address = 'La dirección debe tener al menos 5 caracteres';
      }

      if (!formData.ipAddress.trim()) {
      errors.ipAddress = isXtamMasivos
        ? 'La IP de streaming es requerida'
        : 'La dirección IP es requerida';
    } else if (!validateIP(formData.ipAddress)) {
      errors.ipAddress = isXtamMasivos
        ? 'La IP de streaming no es válida (ej: 192.168.1.100)'
        : 'La dirección IP no es válida (ej: 192.168.1.100)';
    }

    if (isXtamMasivos) {
      if (!formData.recordingsIp.trim()) {
        errors.recordingsIp = 'La IP de grabaciones es requerida';
      } else if (!validateIP(formData.recordingsIp)) {
        errors.recordingsIp = 'La IP de grabaciones no es válida (ej: 192.168.1.100)';
      }
      if (massiveCameras.length < 1) {
        errors.massiveExcel = 'Debe cargar un Excel con al menos una cámara RTSP';
      } else {
        // Sincronizar numCameras con el Excel
        if (formData.numCameras !== massiveCameras.length) {
          setFormData((prev) => ({ ...prev, numCameras: massiveCameras.length }));
        }
      }
    }

    if (formData.neighborhood && formData.neighborhood.trim().length > 100) {
      errors.neighborhood = 'El barrio no puede exceder 100 caracteres';
    }

    if (formData.location && formData.location.trim().length > 100) {
      errors.location = 'La localidad no puede exceder 100 caracteres';
    }

    if (!formData.latitude.trim()) {
      errors.latitude = 'La latitud es requerida';
    } else {
      const lat = parseFloat(formData.latitude);
      if (isNaN(lat) || lat < -90 || lat > 90) {
        errors.latitude = 'La latitud debe estar entre -90 y 90';
      }
    }

    if (!formData.longitude.trim()) {
      errors.longitude = 'La longitud es requerida';
    } else {
      const lng = parseFloat(formData.longitude);
      if (isNaN(lng) || lng < -180 || lng > 180) {
        errors.longitude = 'La longitud debe estar entre -180 y 180';
      }
    }

    setFieldErrors(errors);
    
    if (Object.keys(errors).length > 0) {
      setError('Por favor corrija los errores en el formulario');
      return false;
    }
    
    return true;
  };

  const handleNext = async () => {
    setError(null);
    setFieldErrors({});

    if (currentStep === 1) {
      if (validateStep1()) {
        setCurrentStep(2);
      }
    } else if (currentStep === 2) {
      if (!validateStep2()) return;
      try {
        const checkRes = await axios.get(`${API_URL}/sites/check-ip`, {
          params: {
            ipAddress: formData.ipAddress.trim(),
            ...(editingSite?.id && { excludeSiteId: editingSite.id }),
          },
          headers: authHeaders,
          timeout: HTTP_TIMEOUT_MS,
        });
        if (checkRes.data?.data?.available === false) {
          setError('La dirección IP del sitio ya está en uso por otro sitio.');
          return;
        }
      } catch (e: any) {
        const msg = e.response?.data?.error?.message || e.response?.data?.message;
        if (msg && typeof msg === 'string' && msg.toLowerCase().includes('ip') && msg.toLowerCase().includes('uso')) {
          setError(msg);
          return;
        }
      }
      setCurrentStep(3);
    }
  };

  const handleBack = () => {
    setCurrentStep(currentStep - 1);
    setError(null);
    setFieldErrors({});
  };

  const handleSubmit = async () => {
    setError(null);
    // PRIVADO/EU con XTAM Video deben tener número de cámaras 1-4
    const isPrivadoOrEu = formData.clientServicesId === 1 || formData.clientServicesId === 2;
    if (isPrivadoOrEu && formData.selectedXtamVideos.length > 0 && (formData.numCameras < 1 || formData.numCameras > 4)) {
      setError('Para sitios PRIVADO o EU con XTAM Video debe seleccionar el número de cámaras (1 a 4) en el paso 1.');
      return;
    }
    if (isXtamMasivos) {
      if (massiveCameras.length < 1) {
        setError('Para XTAM MASIVOS debe cargar un Excel con cámaras RTSP en el paso 2.');
        return;
      }
      if (!validateIP(formData.ipAddress) || !validateIP(formData.recordingsIp)) {
        setError('Para XTAM MASIVOS se requieren IP de streaming e IP de grabaciones válidas.');
        return;
      }
    }
    setLoading(true);
    try {
      const payload = {
        name: formData.name,
        address: formData.address,
        neighborhood: formData.neighborhood || undefined,
        location: formData.location || undefined,
        geoDepartment: formData.geoDepartment?.trim() || undefined,
        geoMunicipality: formData.geoMunicipality?.trim() || undefined,
        ipAddress: formData.ipAddress,
        latitude: formData.latitude || undefined,
        longitude: formData.longitude || undefined,
        active: formData.active,
        certificate: formData.certificate,
        clientServicesId: formData.clientServicesId,
        streamingOnDemandEnabled: formData.streamingOnDemandEnabled,
      };

      let siteId: number;

      if (editingSite) {
        // Modo edición
        await axios.put(`${API_URL}/sites/${editingSite.id}`, payload, reqCfg);
        siteId = editingSite.id;
      } else {
        // Modo creación
        const response = await axios.post(`${API_URL}/sites`, payload, reqCfg);
        siteId = response.data.data.id;
      }
      
      // Asignar dispositivos con sus configuraciones
      await assignDevicesWithConfigurations(siteId);
      
      const siteName = formData.name.trim();
      showSuccess(
        editingSite
          ? `Sitio "${siteName}" actualizado correctamente`
          : `Sitio "${siteName}" creado correctamente`
      );

      onSuccess();
      handleClose();
    } catch (e: any) {
      const apiMsg = e.response?.data?.error?.message || e.response?.data?.message || e.message;
      const errorMsg = apiMsg || `Error ${editingSite ? 'actualizando' : 'creando'} sitio`;
      setError(errorMsg);
      showError(errorMsg);
    } finally {
      setLoading(false);
    }
  };
  
  // Timeout aplicado a cada request HTTP del wizard para evitar quedarse "actualizando datos"
  // si el backend cuelga.
  const HTTP_TIMEOUT_MS = 30000;
  const reqCfg = { headers: authHeaders, timeout: HTTP_TIMEOUT_MS };

  // Ejecuta una lista de operaciones HTTP y devuelve los mensajes de error de las fallidas.
  const runDeviceOps = async (
    ops: Array<{ label: string; run: () => Promise<any> }>
  ): Promise<string[]> => {
    if (ops.length === 0) return [];
    const results = await Promise.allSettled(ops.map(o => o.run()));
    const failures: string[] = [];
    results.forEach((r, i) => {
      if (r.status === 'rejected') {
        const err: any = r.reason;
        const apiMsg =
          err?.response?.data?.error?.message ||
          err?.response?.data?.message ||
          err?.message ||
          'Error desconocido';
        failures.push(`${ops[i].label}: ${apiMsg}`);
      }
    });
    return failures;
  };

  // Construye el payload para POST/PUT de cámara. En PUT omitimos onvifPassword vacío
  // para no invalidar el backend (mantiene la contraseña previamente guardada).
  const buildCameraPayload = (
    inventoryId: number,
    config: any,
    forCreate: boolean
  ) => {
    const payload: any = {
      name: config.name,
      urlRtsp: config.urlRtsp,
      isOnvif: config.isOnvif || false,
      onvifUrl: config.onvifUrl,
      onvifUsername: config.onvifUsername,
      latitude: config.latitude && String(config.latitude).trim() ? String(config.latitude).trim() : undefined,
      longitude: config.longitude && String(config.longitude).trim() ? String(config.longitude).trim() : undefined,
      xtamVideoChannel: config.xtamVideoChannel,
      isFromDvr: config.isFromDvr || false,
      isAllowTesting: config.isAllowTesting !== false,
      isAllowAnalytics: config.isAllowAnalytics || false,
      vmsId: config.vmsId,
      sirenDeviceId: config.sirenDeviceId ?? null,
    };
    if (config.siteAddress != null && config.siteAddress !== '') {
      payload.siteAddress = config.siteAddress;
    }
    if (config.ipAddress != null && config.ipAddress !== '') {
      payload.ipAddress = config.ipAddress;
    }
    // Sólo incluir onvifPassword si el usuario efectivamente la ingresó.
    // En modo edición, `loadSiteDevices` la deja como '' por seguridad; enviarla vacía
    // hace que el backend (createDeviceCameraSchema) rechace ONVIF y perdamos la cámara.
    if (config.onvifPassword && String(config.onvifPassword).trim() !== '') {
      payload.onvifPassword = config.onvifPassword;
    }
    if (forCreate) {
      payload.inventoryCamerasId = inventoryId;
    }
    return payload;
  };

  // Sincroniza los dispositivos del sitio con la selección actual usando un enfoque
  // diff/upsert: sólo elimina los desasignados, sólo crea los nuevos y sólo actualiza
  // aquellos cuya configuración el usuario haya modificado explícitamente.
  //
  // Antes se hacía delete-all + recreate: si algún POST fallaba (por validación,
  // timeout, etc.) el sitio quedaba con menos dispositivos que antes, o el flujo se
  // colgaba en "actualizando datos". Este enfoque preserva los dispositivos que el
  // usuario no tocó y propaga los errores hasta handleSubmit.
  const assignDevicesWithConfigurations = async (siteId: number) => {
    const errors: string[] = [];

    // 1. Snapshot del estado actual del sitio (sólo relevante al editar)
    let current: {
      cameras: any[];
      xtamVideos: any[];
      miniots: any[];
      routers: any[];
      buttons: any[];
      sirens: any[];
    } = { cameras: [], xtamVideos: [], miniots: [], routers: [], buttons: [], sirens: [] };

    if (editingSite) {
      try {
        const [c, x, m, r, b, s] = await Promise.all([
          axios.get(`${API_URL}/sites/${siteId}/cameras`, reqCfg),
          axios.get(`${API_URL}/sites/${siteId}/xtam-videos`, reqCfg),
          axios.get(`${API_URL}/sites/${siteId}/miniots`, reqCfg),
          axios.get(`${API_URL}/sites/${siteId}/routers`, reqCfg),
          axios.get(`${API_URL}/sites/${siteId}/buttons`, reqCfg),
          axios.get(`${API_URL}/sites/${siteId}/sirens`, reqCfg),
        ]);
        current = {
          cameras: c.data?.data || [],
          xtamVideos: x.data?.data || [],
          miniots: m.data?.data || [],
          routers: r.data?.data || [],
          buttons: b.data?.data || [],
          sirens: s.data?.data || [],
        };
      } catch (e: any) {
        // Si no podemos leer el inventario actual, ABORTAR antes de tocar nada.
        // De lo contrario podríamos borrar dispositivos que no vimos.
        const msg = e?.response?.data?.error?.message || e?.response?.data?.message || e?.message || 'Error desconocido';
        throw new Error(`No se pudo leer el inventario actual del sitio: ${msg}`);
      }
    }

    const indexByInventory = (rows: any[], invKey: string) => {
      const m = new Map<number, any>();
      rows.forEach(row => {
        const invId = row?.[invKey];
        if (invId != null) m.set(invId, row);
      });
      return m;
    };

    const currentCameras = indexByInventory(current.cameras, 'inventoryCamerasId');
    const currentXtam = indexByInventory(current.xtamVideos, 'inventoryXtamVideosId');
    const currentMiniots = indexByInventory(current.miniots, 'inventoryMiniotsId');
    const currentRouters = indexByInventory(current.routers, 'inventoryRoutersId');
    const currentButtons = indexByInventory(current.buttons, 'inventoryButtonsId');
    const currentSirens = indexByInventory(current.sirens, 'inventorySirensId');

    // === CÁMARAS === (POST nuevo, PUT si el usuario editó su config, DELETE si se desasignó)
    {
      const selected = new Set<number>(formData.selectedCameras);
      const ops: Array<{ label: string; run: () => Promise<any> }> = [];

      currentCameras.forEach((device, invId) => {
        if (!selected.has(invId)) {
          ops.push({
            label: `Eliminar cámara (inv #${invId})`,
            run: () => axios.delete(`${API_URL}/sites/${siteId}/cameras/${device.id}`, reqCfg),
          });
        }
      });

      selected.forEach(invId => {
        const configKey = `cameras_${invId}`;
        const config = deviceConfigurations[configKey];
        const existing = currentCameras.get(invId);
        const isDirty = dirtyConfigKeys.has(configKey);

        if (!existing) {
          // Alta: la cámara sólo se crea si tiene configuración (comportamiento previo)
          if (config) {
            const payload = buildCameraPayload(invId, config, /* forCreate */ true);
            ops.push({
              label: `Crear cámara (inv #${invId})`,
              run: () => axios.post(`${API_URL}/sites/${siteId}/cameras`, payload, reqCfg),
            });
          }
        } else if (isDirty && config) {
          // Actualización: sólo si el usuario abrió el modal y guardó cambios
          const payload = buildCameraPayload(invId, config, /* forCreate */ false);
          ops.push({
            label: `Actualizar cámara (inv #${invId})`,
            run: () => axios.put(`${API_URL}/sites/${siteId}/cameras/${existing.id}`, payload, reqCfg),
          });
        }
        // else: cámara existente sin cambios → no tocar
      });

      errors.push(...(await runDeviceOps(ops)));
    }

    // === XTAM VIDEOS === (backend no expone PUT: cuando el usuario editó config,
    // hacemos DELETE + POST atómico por dispositivo para no perder datos si el POST falla)
    {
      const selected = new Set<number>(formData.selectedXtamVideos);
      const removeOps: Array<{ label: string; run: () => Promise<any> }> = [];
      const addOps: Array<{ label: string; run: () => Promise<any> }> = [];

      currentXtam.forEach((device, invId) => {
        if (!selected.has(invId)) {
          removeOps.push({
            label: `Eliminar XTAM Video (inv #${invId})`,
            run: () => axios.delete(`${API_URL}/sites/${siteId}/xtam-videos/${device.id}`, reqCfg),
          });
        }
      });

      selected.forEach(invId => {
        const configKey = `xtamVideos_${invId}`;
        const config = deviceConfigurations[configKey] || {};
        const existing = currentXtam.get(invId);
        const isDirty = dirtyConfigKeys.has(configKey);

        if (!existing) {
          addOps.push({
            label: `Crear XTAM Video (inv #${invId})`,
            run: () => axios.post(`${API_URL}/sites/${siteId}/xtam-videos`, {
              inventoryXtamVideosId: invId,
              ipAddress: config.ipAddress ?? null,
              state: config.state ?? null,
            }, reqCfg),
          });
        } else if (isDirty) {
          // Delete + create atómico para reflejar la edición
          removeOps.push({
            label: `Reemplazar XTAM Video (inv #${invId}) - borrar`,
            run: async () => {
              await axios.delete(`${API_URL}/sites/${siteId}/xtam-videos/${existing.id}`, reqCfg);
              await axios.post(`${API_URL}/sites/${siteId}/xtam-videos`, {
                inventoryXtamVideosId: invId,
                ipAddress: config.ipAddress ?? null,
                state: config.state ?? null,
              }, reqCfg);
            },
          });
        }
      });

      errors.push(...(await runDeviceOps([...removeOps, ...addOps])));
    }

    // === MINIOT === (sin campos configurables; sólo alta/baja)
    {
      const selected = new Set<number>(formData.selectedMiniots);
      const ops: Array<{ label: string; run: () => Promise<any> }> = [];

      currentMiniots.forEach((device, invId) => {
        if (!selected.has(invId)) {
          ops.push({
            label: `Eliminar MinIoT (inv #${invId})`,
            run: () => axios.delete(`${API_URL}/sites/${siteId}/miniots/${device.id}`, reqCfg),
          });
        }
      });

      selected.forEach(invId => {
        if (!currentMiniots.has(invId)) {
          const configKey = `miniots_${invId}`;
          const config = deviceConfigurations[configKey];
          if (config) {
            ops.push({
              label: `Crear MinIoT (inv #${invId})`,
              run: () => axios.post(`${API_URL}/sites/${siteId}/miniots`, {
                inventoryMiniotsId: invId,
              }, reqCfg),
            });
          }
        }
      });

      errors.push(...(await runDeviceOps(ops)));
    }

    // === ROUTERS === (backend expone PATCH, así que actualizamos en sitio)
    {
      const selected = new Set<number>(formData.selectedRouters);
      const ops: Array<{ label: string; run: () => Promise<any> }> = [];

      currentRouters.forEach((device, invId) => {
        if (!selected.has(invId)) {
          ops.push({
            label: `Eliminar Router (inv #${invId})`,
            run: () => axios.delete(`${API_URL}/sites/${siteId}/routers/${device.id}`, reqCfg),
          });
        }
      });

      selected.forEach(invId => {
        const configKey = `routers_${invId}`;
        const config = deviceConfigurations[configKey];
        const existing = currentRouters.get(invId);
        const isDirty = dirtyConfigKeys.has(configKey);

        if (!existing) {
          if (config) {
            ops.push({
              label: `Crear Router (inv #${invId})`,
              run: () => axios.post(`${API_URL}/sites/${siteId}/routers`, {
                inventoryRoutersId: invId,
                ipaddress: config.ipAddress || null,
              }, reqCfg),
            });
          }
        } else if (isDirty && config) {
          ops.push({
            label: `Actualizar Router (inv #${invId})`,
            run: () => axios.patch(`${API_URL}/sites/${siteId}/routers/${existing.id}`, {
              ipaddress: config.ipAddress || null,
            }, reqCfg),
          });
        }
      });

      errors.push(...(await runDeviceOps(ops)));
    }

    // === BUTTONS === (sin PUT: si el usuario editó, delete+post atómico)
    {
      const selected = new Set<number>(formData.selectedButtons);
      const ops: Array<{ label: string; run: () => Promise<any> }> = [];

      currentButtons.forEach((device, invId) => {
        if (!selected.has(invId)) {
          ops.push({
            label: `Eliminar Botón (inv #${invId})`,
            run: () => axios.delete(`${API_URL}/sites/${siteId}/buttons/${device.id}`, reqCfg),
          });
        }
      });

      selected.forEach(invId => {
        const configKey = `buttons_${invId}`;
        const config = deviceConfigurations[configKey];
        const existing = currentButtons.get(invId);
        const isDirty = dirtyConfigKeys.has(configKey);

        const buildBody = () => ({
          inventoryButtonsId: invId,
          identificationButton: config?.identificationButton || null,
          emailRegister: config?.emailRegister || null,
          cellphoneRegister: config?.cellphoneRegister || null,
          idSubscriber: config?.idSubscriber || null,
          role: config?.role || null,
          dataAuthorization: config?.dataAuthorization || false,
        });

        if (!existing) {
          if (config) {
            ops.push({
              label: `Crear Botón (inv #${invId})`,
              run: () => axios.post(`${API_URL}/sites/${siteId}/buttons`, buildBody(), reqCfg),
            });
          }
        } else if (isDirty && config) {
          ops.push({
            label: `Actualizar Botón (inv #${invId})`,
            run: async () => {
              await axios.delete(`${API_URL}/sites/${siteId}/buttons/${existing.id}`, reqCfg);
              await axios.post(`${API_URL}/sites/${siteId}/buttons`, buildBody(), reqCfg);
            },
          });
        }
      });

      errors.push(...(await runDeviceOps(ops)));
    }

    // === SIRENS === (sin PUT: si el usuario editó, delete+post atómico)
    {
      const selected = new Set<number>(formData.selectedSirens);
      const ops: Array<{ label: string; run: () => Promise<any> }> = [];

      currentSirens.forEach((device, invId) => {
        if (!selected.has(invId)) {
          ops.push({
            label: `Eliminar Sirena (inv #${invId})`,
            run: () => axios.delete(`${API_URL}/sites/${siteId}/sirens/${device.id}`, reqCfg),
          });
        }
      });

      selected.forEach(invId => {
        const configKey = `sirens_${invId}`;
        const config = deviceConfigurations[configKey];
        const existing = currentSirens.get(invId);
        const isDirty = dirtyConfigKeys.has(configKey);

        const buildBody = () => ({
          inventorySirensId: invId,
          ipaddress: config?.ipAddress || null,
        });

        if (!existing) {
          if (config) {
            ops.push({
              label: `Crear Sirena (inv #${invId})`,
              run: () => axios.post(`${API_URL}/sites/${siteId}/sirens`, buildBody(), reqCfg),
            });
          }
        } else if (isDirty && config) {
          ops.push({
            label: `Actualizar Sirena (inv #${invId})`,
            run: async () => {
              await axios.delete(`${API_URL}/sites/${siteId}/sirens/${existing.id}`, reqCfg);
              await axios.post(`${API_URL}/sites/${siteId}/sirens`, buildBody(), reqCfg);
            },
          });
        }
      });

      errors.push(...(await runDeviceOps(ops)));
    }

    // Auto-crear cámaras (inventario + device) para cada XTAM Video (sólo PRIVADO/EU).
    // Se omite si al editar el sitio ya tenía cámaras (comportamiento previo preservado).
    const skipCameraAutoCreation = editingSite && siteHasExistingCameras;

    if (!skipCameraAutoCreation &&
        (formData.clientServicesId === 1 || formData.clientServicesId === 2) &&
        formData.numCameras > 0 &&
        formData.selectedXtamVideos.length > 0) {

      let cameraBrandId = 1;
      let cameraModelId = 1;
      try {
        const [brandsRes, modelsRes] = await Promise.all([
          axios.get(`${API_URL}/inventory/cameras/brands`, reqCfg).catch(() => null),
          axios.get(`${API_URL}/inventory/cameras/models`, reqCfg).catch(() => null),
        ]);
        const brands = brandsRes?.data?.data || [];
        const models = modelsRes?.data?.data || [];
        if (brands.length > 0) cameraBrandId = brands[0].id;
        if (models.length > 0) cameraModelId = models[0].id;
      } catch (_) { /* usar defaults */ }

      const baseLat = formData.latitude ? parseFloat(formData.latitude) : null;
      const baseLng = formData.longitude ? parseFloat(formData.longitude) : null;
      const offsetDeg = 0.0001;

      for (const xtamId of formData.selectedXtamVideos) {
        const xtamDevice = getDeviceById('xtamVideos', xtamId);
        if (!xtamDevice) {
          errors.push(`No se encontró el XTAM ${xtamId} en el inventario`);
          continue;
        }

        const xtamSerial = xtamDevice.serialDevice || xtamDevice.serial || xtamDevice.serial_device || `XTAM${xtamId}`;

        for (let channel = 1; channel <= formData.numCameras; channel++) {
          try {
            const invResponse = await axios.post(`${API_URL}/inventory/cameras/find-or-create`, {
              serial: `${xtamSerial}-camara${channel}`,
              plate: 'N/A',
              mac: null,
              idBrand: cameraBrandId,
              idModel: cameraModelId,
              isActive: true,
            }, reqCfg);

            const inventoryCameraId = invResponse.data?.data?.id ?? invResponse.data?.id;

            if (inventoryCameraId) {
              const latOffset = baseLat != null ? (baseLat + (channel - 1) * offsetDeg).toFixed(6) : undefined;
              const lngOffset = baseLng != null ? (baseLng + (channel - 1) * offsetDeg).toFixed(6) : undefined;

              await axios.post(`${API_URL}/sites/${siteId}/cameras`, {
                inventoryCamerasId: inventoryCameraId,
                name: `${formData.name} CAMARA ${channel}`,
                siteAddress: formData.address,
                ipAddress: formData.ipAddress,
                urlRtsp: `rtsp://${formData.ipAddress}:8554/camara${channel}`,
                latitude: latOffset,
                longitude: lngOffset,
                xtamVideoChannel: `camara${channel}`,
                isFromDvr: false,
                isAllowAnalytics: true,
                isAllowTesting: true,
                vmsId: '0',
                isOnvif: false,
              }, reqCfg);
            } else {
              errors.push(`No se pudo obtener ID de inventario para cámara ${channel}`);
            }
          } catch (err: any) {
            const msg = err.response?.data?.error?.message || err.response?.data?.message || err.message || 'Error desconocido';
            errors.push(`Cámara ${channel} (XTAM ${xtamSerial}): ${msg}`);
          }
        }
      }
    }

    if (isXtamMasivos && !(editingSite && siteHasExistingCameras) && massiveCameras.length > 0) {
      const errs = await provisionXtamMasivosSite({
        apiUrl: API_URL,
        authHeaders,
        siteId,
        siteName: formData.name,
        siteAddress: formData.address,
        streamingIp: formData.ipAddress.trim(),
        recordingsIp: formData.recordingsIp.trim(),
        cameras: massiveCameras,
        latitude: formData.latitude,
        longitude: formData.longitude,
      });
      errors.push(...errs);
    }

    if (errors.length > 0) {
      throw new Error(errors.join('; '));
    }
  };

  const handleClose = () => {
    setCurrentStep(1);
    setFormData({
      clientServicesId: 1, // 1: PRIVADO
      name: '',
      address: '',
      neighborhood: '',
      location: '',
      geoDepartment: '',
      geoMunicipality: '',
      ipAddress: '',
      recordingsIp: '',
      latitude: '',
      longitude: '',
      active: true,
      certificate: false,
      streamingOnDemandEnabled: true,
      numCameras: 0,
      selectedCameras: [],
      selectedXtamVideos: [],
      selectedMiniots: [],
      selectedButtons: [],
      selectedSirens: [],
      selectedRouters: [],
    });
    setMassiveCameras([]);
    setMassiveExcelName('');
    setMassiveExcelError(null);
    setError(null);
    setFieldErrors({});
    setTouched({});
    setSelectedDeviceType(null);
    setShowDeviceSelector(false);
    setDeviceConfigurations({});
    setDirtyConfigKeys(new Set());
    setShowConfigModal(false);
    setCurrentConfigDevice(null);
    setSiteHasExistingCameras(false);
    onClose();
  };

  // Manejar blur para marcar campo como tocado
  const handleBlur = (fieldName: string) => {
    setTouched({ ...touched, [fieldName]: true });
  };

  // Validar campo individual
  const validateField = (fieldName: string, value: any) => {
    const errors = { ...fieldErrors };

    switch (fieldName) {
      case 'name':
        if (!value.trim()) {
          errors.name = 'El nombre del sitio es requerido';
        } else if (value.trim().length < 3) {
          errors.name = 'El nombre debe tener al menos 3 caracteres';
        } else if (value.trim().length > 100) {
          errors.name = 'El nombre no puede exceder 100 caracteres';
        } else {
          delete errors.name;
        }
        break;

      case 'address':
        if (!value.trim()) {
          errors.address = 'La dirección es requerida';
        } else if (value.trim().length < 5) {
          errors.address = 'La dirección debe tener al menos 5 caracteres';
        } else {
          delete errors.address;
        }
        break;

      case 'ipAddress':
        if (!value.trim()) {
          errors.ipAddress = isXtamMasivos
            ? 'La IP de streaming es requerida'
            : 'La dirección IP es requerida';
        } else if (!validateIP(value)) {
          errors.ipAddress = isXtamMasivos
            ? 'La IP de streaming no es válida (ej: 192.168.1.100)'
            : 'La dirección IP no es válida (ej: 192.168.1.100)';
        } else {
          delete errors.ipAddress;
        }
        break;

      case 'recordingsIp':
        if (!value.trim()) {
          errors.recordingsIp = 'La IP de grabaciones es requerida';
        } else if (!validateIP(value)) {
          errors.recordingsIp = 'La IP de grabaciones no es válida (ej: 192.168.1.100)';
        } else {
          delete errors.recordingsIp;
        }
        break;

      case 'neighborhood':
        if (value && value.trim().length > 100) {
          errors.neighborhood = 'El barrio no puede exceder 100 caracteres';
        } else {
          delete errors.neighborhood;
        }
        break;

      case 'location':
        if (value && value.trim().length > 100) {
          errors.location = 'La localidad no puede exceder 100 caracteres';
        } else {
          delete errors.location;
        }
        break;

      case 'latitude':
        if (!value || !value.trim()) {
          errors.latitude = 'La latitud es requerida';
        } else {
          const lat = parseFloat(value);
          if (isNaN(lat) || lat < -90 || lat > 90) {
            errors.latitude = 'La latitud debe estar entre -90 y 90';
          } else {
            delete errors.latitude;
          }
        }
        break;

      case 'longitude':
        if (!value || !value.trim()) {
          errors.longitude = 'La longitud es requerida';
        } else {
          const lng = parseFloat(value);
          if (isNaN(lng) || lng < -180 || lng > 180) {
            errors.longitude = 'La longitud debe estar entre -180 y 180';
          } else {
            delete errors.longitude;
          }
        }
        break;
    }

    setFieldErrors(errors);
  };

  // Función para abrir el selector de un tipo de dispositivo
  const handleOpenDeviceSelector = (deviceType: DeviceType) => {
    setSelectedDeviceType(deviceType);
    setShowDeviceSelector(true);
  };

  // Función para cerrar el selector
  const handleCloseDeviceSelector = () => {
    setShowDeviceSelector(false);
    setSelectedDeviceType(null);
  };

  // Función para obtener los dispositivos del inventario según el tipo
  const getDevicesByType = (deviceType: DeviceType): InventoryItem[] => {
    switch (deviceType) {
      case 'cameras': return inventoryCameras;
      case 'xtamVideos': return inventoryXtamVideos;
      case 'miniots': return inventoryMiniots;
      case 'routers': return inventoryRouters;
      case 'buttons': return inventoryButtons;
      case 'sirens': return inventorySirens;
      default: return [];
    }
  };

  // Función para obtener IDs seleccionados según el tipo
  const getSelectedIdsByType = (deviceType: DeviceType): number[] => {
    switch (deviceType) {
      case 'cameras': return formData.selectedCameras;
      case 'xtamVideos': return formData.selectedXtamVideos;
      case 'miniots': return formData.selectedMiniots;
      case 'routers': return formData.selectedRouters;
      case 'buttons': return formData.selectedButtons;
      case 'sirens': return formData.selectedSirens;
      default: return [];
    }
  };

  // Función para actualizar IDs seleccionados
  const handleSelectionChange = (deviceType: DeviceType, ids: number[]) => {
    const previousIds = getSelectedIdsByType(deviceType);
    
    // Detectar nuevos dispositivos agregados
    const newIds = ids.filter(id => !previousIds.includes(id));
    
    // Actualizar la selección usando función callback para preservar el estado
    setFormData(prev => {
      switch (deviceType) {
        case 'cameras':
          return { ...prev, selectedCameras: ids };
        case 'xtamVideos':
          return { ...prev, selectedXtamVideos: ids };
        case 'miniots':
          return { ...prev, selectedMiniots: ids };
        case 'routers':
          return { ...prev, selectedRouters: ids };
        case 'buttons':
          return { ...prev, selectedButtons: ids };
        case 'sirens':
          return { ...prev, selectedSirens: ids };
        default:
          return prev;
      }
    });
    
    // Si se agregó un nuevo dispositivo, abrir automáticamente su modal de configuración
    if (newIds.length > 0) {
      const newDeviceId = newIds[0];
      
      const configKey = `${deviceType}_${newDeviceId}`;
      const hasExistingConfig = !!deviceConfigurations[configKey];
      
      // XTAM Videos tienen configuración opcional, no forzar el modal
      const typesWithOptionalConfig: DeviceType[] = ['xtamVideos'];
      const isConfigOptional = typesWithOptionalConfig.includes(deviceType);

      if (!hasExistingConfig && !isConfigOptional) {
        setIsConfigRequired(true);
      }
      
      if (!isConfigOptional) {
        setTimeout(() => {
          handleConfigureDevice(deviceType, newDeviceId);
        }, 100);
      }
    }
  };

  // Función para remover un dispositivo específico
  const handleRemoveDevice = (deviceType: DeviceType, deviceId: number) => {
    const currentIds = getSelectedIdsByType(deviceType);
    handleSelectionChange(deviceType, currentIds.filter(id => id !== deviceId));

    // También eliminar su configuración
    const configKey = `${deviceType}_${deviceId}`;
    const newConfigs = { ...deviceConfigurations };
    delete newConfigs[configKey];
    setDeviceConfigurations(newConfigs);
    setDirtyConfigKeys(prev => {
      if (!prev.has(configKey)) return prev;
      const next = new Set(prev);
      next.delete(configKey);
      return next;
    });
  };

  // Función para obtener información de dispositivo por ID
  const getDeviceById = (deviceType: DeviceType, deviceId: number): InventoryItem | undefined => {
    const devices = getDevicesByType(deviceType);
    return devices.find(d => d.id === deviceId);
  };
  
  // Función para abrir el modal de configuración de dispositivo
  const handleConfigureDevice = async (deviceType: DeviceType, deviceId: number) => {
    const device = getDeviceById(deviceType, deviceId);
    if (!device) return;
    
    // Mapear tipo de dispositivo al tipo de configuración
    let configType: 'camera' | 'xtamVideo' | 'miniot' | 'router' | 'button' | 'siren';
    let apiEndpoint = '';
    
    switch (deviceType) {
      case 'cameras':
        configType = 'camera';
        apiEndpoint = 'cameras';
        break;
      case 'xtamVideos':
        configType = 'xtamVideo';
        apiEndpoint = 'xtam-videos';
        break;
      case 'miniots':
        configType = 'miniot';
        apiEndpoint = 'miniots';
        break;
      case 'routers':
        configType = 'router';
        apiEndpoint = 'routers';
        break;
      case 'buttons':
        configType = 'button';
        apiEndpoint = 'buttons';
        break;
      case 'sirens':
        configType = 'siren';
        apiEndpoint = 'sirens';
        break;
      default:
        return;
    }
    
    // Verificar si ya existe configuración local
    const configKey = `${deviceType}_${deviceId}`;
    let existingConfig = deviceConfigurations[configKey];
    
    // Si no existe local, intentar cargar desde el backend (dispositivo ya asignado a un sitio)
    if (!existingConfig && device.siteId) {
      try {
        // Mapeo de tipo de dispositivo a nombre de campo de inventario en el DTO
        const inventoryKeyMap: Record<string, string> = {
          cameras: 'inventoryCamerasId',
          xtamVideos: 'inventoryXtamVideosId',
          miniots: 'inventoryMiniotsId',
          routers: 'inventoryRoutersId',
          buttons: 'inventoryButtonsId',
          sirens: 'inventorySirensId',
        };
        
        const inventoryKey = inventoryKeyMap[deviceType];
        
        // Usar endpoint basado en sitio: GET /sites/:siteId/:endpoint
        const response = await axios.get(
          `${API_URL}/sites/${device.siteId}/${apiEndpoint}`,
          { headers: authHeaders }
        );
        
        const devices = response.data?.data || [];
        const deviceData = devices.find((d: any) => d[inventoryKey] === deviceId);
        
        if (deviceData) {
          // Mapear los datos del backend al formato de configuración según el tipo
          switch (deviceType) {
            case 'cameras':
              existingConfig = {
                name: deviceData.name,
                siteAddress: deviceData.siteAddress || '',
                urlRtsp: deviceData.urlRtsp,
                isOnvif: deviceData.isOnvif,
                onvifUrl: deviceData.onvifUrl,
                onvifUsername: deviceData.onvifUsername,
                onvifPassword: '',
                latitude: deviceData.latitude != null ? String(deviceData.latitude) : '',
                longitude: deviceData.longitude != null ? String(deviceData.longitude) : '',
                xtamVideoChannel: deviceData.xtamVideoChannel,
                isFromDvr: deviceData.isFromDvr,
                isAllowTesting: deviceData.isAllowTesting ?? true,
                isAllowAnalytics: deviceData.isAllowAnalytics ?? false,
                vmsId: deviceData.vmsId,
              };
              break;
            case 'xtamVideos':
              existingConfig = {
                ipAddress: deviceData.ipAddress || null,
              };
              break;
            case 'routers':
              existingConfig = {
                ipAddress: deviceData.ipaddress || null,
              };
              break;
            case 'sirens':
              existingConfig = {
                ipAddress: deviceData.ipaddress || null,
              };
              break;
            case 'buttons':
              existingConfig = {
                identificationButton: deviceData.identificationButton || null,
                emailRegister: deviceData.emailRegister || null,
                cellphoneRegister: deviceData.cellphoneRegister || null,
                idSubscriber: deviceData.idSubscriber || null,
                role: deviceData.role || null,
                dataAuthorization: deviceData.dataAuthorization || false,
              };
              break;
            case 'miniots':
              existingConfig = {};
              break;
          }
          
          // Guardar la configuración cargada para que esté disponible en el modal
          setDeviceConfigurations(prev => ({
            ...prev,
            [configKey]: existingConfig
          }));
        }
      } catch (e) {
        console.log('No existing device configuration found, will create new');
      }
    }
    
    setCurrentConfigDevice({
      type: deviceType,
      id: deviceId,
      info: {
        id: device.id,
        serial: device.serial || device.serialDevice || device.serial_device || 'Sin serial',
        brand: device.brandName,
        model: device.modelName || device.model_name
      }
    });
    setShowConfigModal(true);
  };
  
  // Función para guardar configuración de dispositivo
  const handleSaveDeviceConfig = (config: any) => {
    if (currentConfigDevice) {
      const configKey = `${currentConfigDevice.type}_${currentConfigDevice.id}`;
      setDeviceConfigurations({
        ...deviceConfigurations,
        [configKey]: config
      });
      setDirtyConfigKeys(prev => {
        const next = new Set(prev);
        next.add(configKey);
        return next;
      });
    }
    setShowConfigModal(false);
    setCurrentConfigDevice(null);
    setIsConfigRequired(false);
  };

  // Función para cancelar y deseleccionar dispositivo
  const handleCancelDeviceConfig = () => {
    if (!currentConfigDevice) return;

    // Deseleccionar el dispositivo del formData
    const deviceType = currentConfigDevice.type;
    const deviceId = currentConfigDevice.id;
    const currentIds = getSelectedIdsByType(deviceType);
    
    // Remover el dispositivo de la selección
    handleSelectionChange(deviceType, currentIds.filter(id => id !== deviceId));

    // Cerrar el modal
    setShowConfigModal(false);
    setCurrentConfigDevice(null);
    setIsConfigRequired(false);
  };
  
  // Función para verificar si un dispositivo tiene configuración
  const hasConfiguration = (deviceType: DeviceType, deviceId: number): boolean => {
    const configKey = `${deviceType}_${deviceId}`;
    return !!deviceConfigurations[configKey];
  };

  // Función para abrir la página de inventario en nueva pestaña
  const handleCreateNewDevice = () => {
    window.open('/admin/inventory', '_blank');
  };

  // Función para cerrar el modal de creación de dispositivo
  const handleCloseCreateDeviceModal = () => {
    setShowCreateDeviceModal(false);
  };

  // Función para después de crear un dispositivo
  const handleDeviceCreated = async () => {
    setShowCreateDeviceModal(false);
    // Recargar inventarios
    await loadInventories();
  };

  // Función para manejar solicitud de reasignación
  const handleReassignRequest = (deviceId: number, fromSiteId: number, fromSiteName: string) => {
    setDeviceToReassign({ deviceId, fromSiteId, fromSiteName });
    setShowReassignModal(true);
  };

  // Función para confirmar reasignación
  const handleConfirmReassign = () => {
    if (deviceToReassign && selectedDeviceType) {
      const currentIds = getSelectedIdsByType(selectedDeviceType);
      
      // Verificar límite máximo
      if (selectedDeviceType === 'cameras' && 
          (formData.clientServicesId === 1 || formData.clientServicesId === 2) && 
          currentIds.length >= MAX_CAMERAS) {
        setError(`Solo puedes seleccionar un máximo de ${MAX_CAMERAS} cámaras`);
        setShowReassignModal(false);
        setDeviceToReassign(null);
        return;
      }

      // Agregar el dispositivo a la selección
      if (!currentIds.includes(deviceToReassign.deviceId)) {
        handleSelectionChange(selectedDeviceType, [...currentIds, deviceToReassign.deviceId]);
      }
      
      setShowReassignModal(false);
      setDeviceToReassign(null);
    }
  };

  // Función para cancelar reasignación
  const handleCancelReassign = () => {
    setShowReassignModal(false);
    setDeviceToReassign(null);
  };

  // Configuración de tipos de dispositivos disponibles según el tipo de sitio
  const getAvailableDeviceTypes = (): Array<{
    type: DeviceType;
    title: string;
    description: string;
    icon: React.ReactNode;
    variant: 'primary' | 'secondary' | 'success' | 'warning' | 'info';
  }> => {
    const allTypes = [
      {
        type: 'cameras' as DeviceType,
        title: 'Cámaras',
        description: formData.clientServicesId === 3 ? 'Sin límite' : `Máximo ${MAX_CAMERAS}`,
        icon: <Camera size={24} />,
        variant: 'primary' as const,
      },
      {
        type: 'xtamVideos' as DeviceType,
        title: 'XTAM Video',
        description: (formData.clientServicesId === 1 || formData.clientServicesId === 2) ? 'Máximo 1 por sitio' : 'Grabadores de video',
        icon: <Server size={24} />,
        variant: 'secondary' as const,
      },
      {
        type: 'miniots' as DeviceType,
        title: 'MinIoT',
        description: 'Dispositivos IoT',
        icon: <Wifi size={24} />,
        variant: 'info' as const,
      },
      {
        type: 'routers' as DeviceType,
        title: 'Routers',
        description: formData.clientServicesId === 3 ? 'Opcional' : 'Conectividad de red',
        icon: <Router size={24} />,
        variant: 'success' as const,
      },
      {
        type: 'buttons' as DeviceType,
        title: 'Botones de Pánico',
        description: 'Dispositivos de emergencia',
        icon: <AlertCircle size={24} />,
        variant: 'warning' as const,
      },
      {
        type: 'sirens' as DeviceType,
        title: 'Sirenas',
        description: 'Sistemas de alerta',
        icon: <Volume2 size={24} />,
        variant: 'warning' as const,
      },
    ];

    // Filtrar según el tipo de sitio
    // 1: PRIVADO, 2: EU, 3: PVVC, 4: XTAM MASIVOS
    // En creación, PRIVADO/EU/MASIVOS no muestran Cámaras (se crean automáticamente)
    const isPrivadoOrEu = formData.clientServicesId === 1 || formData.clientServicesId === 2;
    const hideCamerasCard = !editingSite && (isPrivadoOrEu || isXtamMasivos);

    if (formData.clientServicesId === 3) { // PVVC
      return allTypes.filter(t => t.type === 'cameras' || t.type === 'routers');
    } else if (formData.clientServicesId === 1 || isXtamMasivos) { // PRIVADO / MASIVOS
      const types = ['xtamVideos', 'miniots', 'routers'];
      if (!hideCamerasCard) types.unshift('cameras');
      return allTypes.filter(t => types.includes(t.type));
    } else { // EU (2)
      return allTypes.filter(t => !hideCamerasCard || t.type !== 'cameras');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="wizard-overlay">
      <div className="wizard-container">
        {/* Header */}
        <div className="wizard-header">
          <div>
            <h2 className="wizard-title">{editingSite ? 'Editar Sitio' : 'Crear Nuevo Sitio'}</h2>
            <p className="wizard-subtitle">Paso {currentStep} de 3</p>
          </div>
          <button onClick={handleClose} className="wizard-close-btn" disabled={loading}>
            <X size={24} />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="wizard-progress">
          <div className="wizard-step-indicator">
            <div className={`wizard-step-number ${currentStep === 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`}>
              {currentStep > 1 ? '✓' : '1'}
            </div>
            <span className={`wizard-step-label ${currentStep === 1 ? 'active' : ''}`}>
              Tipo de sitio
            </span>
          </div>

          <div className="wizard-step-divider"></div>

          <div className="wizard-step-indicator">
            <div className={`wizard-step-number ${currentStep === 2 ? 'active' : ''} ${currentStep > 2 ? 'completed' : ''}`}>
              {currentStep > 2 ? '✓' : '2'}
            </div>
            <span className={`wizard-step-label ${currentStep === 2 ? 'active' : ''}`}>
              Información
            </span>
          </div>

          <div className="wizard-step-divider"></div>

          <div className="wizard-step-indicator">
            <div className={`wizard-step-number ${currentStep === 3 ? 'active' : ''}`}>
              3
            </div>
            <span className={`wizard-step-label ${currentStep === 3 ? 'active' : ''}`}>
              {isXtamMasivos ? 'XTAM Server y cámaras' : 'Dispositivos'}
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="wizard-content">
          {error && (
            <div className="wizard-error">
              <AlertCircle size={16} />
              {error}
            </div>
          )}

          {/* Step 1: Site Type */}
          {currentStep === 1 && (
            <div className="wizard-step-content">
              <div className="wizard-step-header">
                <h3 className="wizard-step-title">Seleccione el tipo de sitio</h3>
                <p className="wizard-step-description">
                  El tipo de sitio determinará qué dispositivos puede asignar
                </p>
              </div>

              <div className="site-type-grid">
                {/* PRIVADO - ID: 1 */}
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, clientServicesId: 1 })}
                  className={`site-type-card ${formData.clientServicesId === 1 ? 'selected' : ''}`}
                >
                  <div className={`site-type-icon ${formData.clientServicesId === 1 ? 'selected' : ''}`}>
                    <Building2 size={32} />
                  </div>
                  <div className="site-type-title">PRIVADO</div>
                  <div className="site-type-description">XTAM + Cámaras + MinIoT + Routers</div>
                  {formData.clientServicesId === 1 && (
                    <CheckCircle size={20} className="site-type-check" />
                  )}
                </button>

                {/* EU - ID: 2 */}
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, clientServicesId: 2 })}
                  className={`site-type-card ${formData.clientServicesId === 2 ? 'selected' : ''}`}
                >
                  <div className={`site-type-icon ${formData.clientServicesId === 2 ? 'selected' : ''}`}>
                    <Shield size={32} />
                  </div>
                  <div className="site-type-title">EU</div>
                  <div className="site-type-description">Todo + Botones + Sirenas</div>
                  {formData.clientServicesId === 2 && (
                    <CheckCircle size={20} className="site-type-check" />
                  )}
                </button>

                {/* PVVC - ID: 3 */}
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, clientServicesId: 3, numCameras: 0 })}
                  className={`site-type-card ${formData.clientServicesId === 3 ? 'selected' : ''}`}
                >
                  <div className={`site-type-icon ${formData.clientServicesId === 3 ? 'selected' : ''}`}>
                    <Video size={32} />
                  </div>
                  <div className="site-type-title">PVVC</div>
                  <div className="site-type-description">Cámaras + Routers (opcional)</div>
                  {formData.clientServicesId === 3 && (
                    <CheckCircle size={20} className="site-type-check" />
                  )}
                </button>

                {/* XTAM MASIVOS - ID: 4 */}
                <button
                  type="button"
                  onClick={() => {
                    setFormData({ ...formData, clientServicesId: SITE_SERVICE_XTAM_MASIVOS, numCameras: 0 });
                    setMassiveCameras([]);
                    setMassiveExcelName('');
                    setMassiveExcelError(null);
                  }}
                  className={`site-type-card ${formData.clientServicesId === SITE_SERVICE_XTAM_MASIVOS ? 'selected' : ''}`}
                >
                  <div className={`site-type-icon ${formData.clientServicesId === SITE_SERVICE_XTAM_MASIVOS ? 'selected' : ''}`}>
                    <Server size={32} />
                  </div>
                  <div className="site-type-title">XTAM MASIVOS</div>
                  <div className="site-type-description">XTAM Server + Excel de cámaras</div>
                  {formData.clientServicesId === SITE_SERVICE_XTAM_MASIVOS && (
                    <CheckCircle size={20} className="site-type-check" />
                  )}
                </button>
              </div>

              {(formData.clientServicesId === 1 || formData.clientServicesId === 2) && (
                <>
                  <div className="wizard-form-field" style={{ marginTop: '24px', marginBottom: '28px', maxWidth: '320px' }}>
                    <label className="wizard-form-label">Número de cámaras (XTAM Video)</label>
                    <p style={{ color: '#9ca3af', fontSize: '12px', margin: '0 0 8px 0' }}>
                      Al agregar un XTAM Video, se crearán automáticamente las cámaras según el número seleccionado.
                    </p>
                    <select
                      value={formData.numCameras}
                      onChange={(e) => {
                        setFormData({ ...formData, numCameras: parseInt(e.target.value) });
                        if (fieldErrors.numCameras) setFieldErrors({ ...fieldErrors, numCameras: '' });
                      }}
                      className={`wizard-input ${fieldErrors.numCameras ? 'input-error' : ''}`}
                    >
                      <option value={0}>Seleccione...</option>
                      <option value={1}>1 cámara</option>
                      <option value={2}>2 cámaras</option>
                      <option value={3}>3 cámaras</option>
                      <option value={4}>4 cámaras</option>
                    </select>
                    {fieldErrors.numCameras && (
                      <span className="wizard-field-error">{fieldErrors.numCameras}</span>
                    )}
                  </div>
                  <div className="wizard-form-field" style={{ marginTop: '0', marginBottom: '24px', maxWidth: '520px' }}>
                    <label className="wizard-form-label">Streaming bajo demanda</label>
                    <p style={{ color: '#9ca3af', fontSize: '12px', margin: '0 0 8px 0' }}>
                      Si está activo, la vista de streaming en vivo pausa la transmisión tras el tiempo configurado en el despliegue (típico para LTE). Desactívelo si el sitio usa conectividad fija y no necesita ese límite.
                    </p>
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', color: '#e5e7eb', fontSize: '14px', lineHeight: 1.45 }}>
                      <input
                        type="checkbox"
                        checked={formData.streamingOnDemandEnabled}
                        onChange={(e) =>
                          setFormData({ ...formData, streamingOnDemandEnabled: e.target.checked })
                        }
                        style={{ marginTop: '3px' }}
                      />
                      <span>Activar pausa automática del stream tras el tiempo configurado</span>
                    </label>
                  </div>
                </>
              )}

              <div className="wizard-info-box">
                <h4 className="wizard-info-title">Información:</h4>
                {formData.clientServicesId === 3 && (
                  <p className="wizard-info-text">
                    Los sitios PVVC solo pueden tener cámaras y routers (opcional).
                  </p>
                )}
                {formData.clientServicesId === 1 && (
                  <p className="wizard-info-text">
                    Los sitios PRIVADOS pueden tener XTAM Video, cámaras (máximo {MAX_CAMERAS}), MinIoT y routers.
                  </p>
                )}
                {formData.clientServicesId === 2 && (
                  <p className="wizard-info-text">
                    Los sitios EU tienen acceso a todos los dispositivos: XTAM Video, cámaras (máximo {MAX_CAMERAS}), MinIoT, routers, botones de pánico y sirenas.
                  </p>
                )}
                {isXtamMasivos && (
                  <p className="wizard-info-text">
                    XTAM MASIVOS crea un XTAM Server y las cámaras desde un Excel en el paso 2 (IP de streaming + IP de grabaciones).
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Site Information */}
          {currentStep === 2 && (
            <div className="wizard-step-content">
              <div className="wizard-step-header">
                <h3 className="wizard-step-title">Información del Sitio</h3>
                <p className="wizard-step-description">
                  Complete los datos básicos del sitio
                </p>
              </div>

              <div className="wizard-form-grid">
                <div className="wizard-form-field wizard-form-field-full">
                  <label className="wizard-form-label">Nombre del sitio *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => {
                      setFormData({ ...formData, name: e.target.value });
                      if (touched.name) {
                        validateField('name', e.target.value);
                      }
                    }}
                    onBlur={() => {
                      handleBlur('name');
                      validateField('name', formData.name);
                    }}
                    className={`wizard-input ${fieldErrors.name && touched.name ? 'input-error' : ''}`}
                    placeholder="Ej: Sitio Centro"
                  />
                  {fieldErrors.name && touched.name && (
                    <span className="wizard-field-error">{fieldErrors.name}</span>
                  )}
                </div>

                <div className="wizard-form-field wizard-form-field-full">
                  <label className="wizard-form-label">Dirección *</label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => {
                      setFormData({ ...formData, address: e.target.value });
                      if (touched.address) {
                        validateField('address', e.target.value);
                      }
                    }}
                    onBlur={() => {
                      handleBlur('address');
                      validateField('address', formData.address);
                    }}
                    className={`wizard-input ${fieldErrors.address && touched.address ? 'input-error' : ''}`}
                    placeholder="Ej: Calle 26 #13-45"
                  />
                  {fieldErrors.address && touched.address && (
                    <span className="wizard-field-error">{fieldErrors.address}</span>
                  )}
                </div>

                <div className="wizard-form-field">
                  <label className="wizard-form-label">Barrio</label>
                  <input
                    type="text"
                    value={formData.neighborhood}
                    onChange={(e) => {
                      setFormData({ ...formData, neighborhood: e.target.value });
                      if (touched.neighborhood) {
                        validateField('neighborhood', e.target.value);
                      }
                    }}
                    onBlur={() => {
                      handleBlur('neighborhood');
                      validateField('neighborhood', formData.neighborhood);
                    }}
                    className={`wizard-input ${fieldErrors.neighborhood && touched.neighborhood ? 'input-error' : ''}`}
                    placeholder="Ej: Centro"
                  />
                  {fieldErrors.neighborhood && touched.neighborhood && (
                    <span className="wizard-field-error">{fieldErrors.neighborhood}</span>
                  )}
                </div>

                <div className="wizard-form-field">
                  <label className="wizard-form-label">Localidad</label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => {
                      setFormData({ ...formData, location: e.target.value });
                      if (touched.location) {
                        validateField('location', e.target.value);
                      }
                    }}
                    onBlur={() => {
                      handleBlur('location');
                      validateField('location', formData.location);
                    }}
                    className={`wizard-input ${fieldErrors.location && touched.location ? 'input-error' : ''}`}
                    placeholder="Ej: Bogotá"
                  />
                  {fieldErrors.location && touched.location && (
                    <span className="wizard-field-error">{fieldErrors.location}</span>
                  )}
                </div>

                <div className="wizard-form-field wizard-form-field-full">
                  <label className="wizard-form-label">Departamento (certificado)</label>
                  <input
                    type="text"
                    value={formData.geoDepartment}
                    onChange={(e) => setFormData({ ...formData, geoDepartment: e.target.value })}
                    className="wizard-input"
                    placeholder="Ej: Bogotá D.C. — aparece en PDF de descarga"
                  />
                </div>

                <div className="wizard-form-field wizard-form-field-full">
                  <label className="wizard-form-label">Municipio / ciudad (certificado)</label>
                  <input
                    type="text"
                    value={formData.geoMunicipality}
                    onChange={(e) => setFormData({ ...formData, geoMunicipality: e.target.value })}
                    className="wizard-input"
                    placeholder="Ej: Bogotá D.C. — aparece en PDF de descarga"
                  />
                </div>

                <div className="wizard-form-field">
                  <label className="wizard-form-label">Latitud *</label>
                  <input
                    type="text"
                    value={formData.latitude}
                    onChange={(e) => {
                      setFormData({ ...formData, latitude: e.target.value });
                      if (touched.latitude) {
                        validateField('latitude', e.target.value);
                      }
                    }}
                    onBlur={() => {
                      handleBlur('latitude');
                      validateField('latitude', formData.latitude);
                    }}
                    className={`wizard-input ${fieldErrors.latitude && touched.latitude ? 'input-error' : ''}`}
                    placeholder="Ej: 4.7110"
                  />
                  {fieldErrors.latitude && touched.latitude && (
                    <span className="wizard-field-error">{fieldErrors.latitude}</span>
                  )}
                </div>

                <div className="wizard-form-field">
                  <label className="wizard-form-label">Longitud *</label>
                  <div className="wizard-input-with-button">
                    <input
                      type="text"
                      value={formData.longitude}
                      onChange={(e) => {
                        setFormData({ ...formData, longitude: e.target.value });
                        if (touched.longitude) {
                          validateField('longitude', e.target.value);
                        }
                      }}
                      onBlur={() => {
                        handleBlur('longitude');
                        validateField('longitude', formData.longitude);
                      }}
                      className={`wizard-input ${fieldErrors.longitude && touched.longitude ? 'input-error' : ''}`}
                      placeholder="Ej: -74.0721"
                    />
                    <button
                      type="button"
                      onClick={() => setShowLocationPicker(true)}
                      className="wizard-location-btn"
                      title="Seleccionar en mapa"
                    >
                      <MapIcon size={20} />
                    </button>
                  </div>
                  {fieldErrors.longitude && touched.longitude && (
                    <span className="wizard-field-error">{fieldErrors.longitude}</span>
                  )}
                </div>

                <div className="wizard-form-field">
                  <label className="wizard-form-label">
                    {isXtamMasivos ? 'IP de streaming *' : 'IP del sitio *'}
                  </label>
                  <input
                    type="text"
                    value={formData.ipAddress}
                    onChange={(e) => {
                      setFormData({ ...formData, ipAddress: e.target.value });
                      if (touched.ipAddress) {
                        validateField('ipAddress', e.target.value);
                      }
                    }}
                    onBlur={() => {
                      handleBlur('ipAddress');
                      validateField('ipAddress', formData.ipAddress);
                    }}
                    className={`wizard-input ${fieldErrors.ipAddress && touched.ipAddress ? 'input-error' : ''}`}
                    placeholder="Ej: 192.168.1.100"
                  />
                  {fieldErrors.ipAddress && touched.ipAddress && (
                    <span className="wizard-field-error">{fieldErrors.ipAddress}</span>
                  )}
                </div>

                {isXtamMasivos && (
                  <>
                    <div className="wizard-form-field">
                      <label className="wizard-form-label">IP de grabaciones *</label>
                      <input
                        type="text"
                        value={formData.recordingsIp}
                        onChange={(e) => {
                          setFormData({ ...formData, recordingsIp: e.target.value });
                          if (touched.recordingsIp) {
                            validateField('recordingsIp', e.target.value);
                          }
                        }}
                        onBlur={() => {
                          handleBlur('recordingsIp');
                          validateField('recordingsIp', formData.recordingsIp);
                        }}
                        className={`wizard-input ${fieldErrors.recordingsIp && touched.recordingsIp ? 'input-error' : ''}`}
                        placeholder="Ej: 192.168.1.200"
                      />
                      {fieldErrors.recordingsIp && touched.recordingsIp && (
                        <span className="wizard-field-error">{fieldErrors.recordingsIp}</span>
                      )}
                    </div>

                    <div className="wizard-form-field wizard-form-field-full">
                      <label className="wizard-form-label">
                        <FileSpreadsheet size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                        Excel de cámaras *
                      </label>
                      <p style={{ color: '#9ca3af', fontSize: '12px', margin: '0 0 8px 0' }}>
                        Columnas: RTSP (obligatoria), nombre y tipo de grabación (opcionales). Formatos: .xlsx, .xls, .csv
                      </p>
                      <input
                        ref={massiveFileRef}
                        type="file"
                        accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          setMassiveExcelError(null);
                          if (!file) {
                            setMassiveCameras([]);
                            setMassiveExcelName('');
                            setFormData((prev) => ({ ...prev, numCameras: 0 }));
                            return;
                          }
                          try {
                            const rows = await parseMassiveCamerasExcel(file);
                            if (!rows.length) {
                              setMassiveCameras([]);
                              setMassiveExcelName(file.name);
                              setMassiveExcelError('No se encontraron URLs RTSP válidas en el archivo.');
                              setFormData((prev) => ({ ...prev, numCameras: 0 }));
                              return;
                            }
                            setMassiveCameras(rows);
                            setMassiveExcelName(file.name);
                            setMassiveExcelError(null);
                            setFormData((prev) => ({ ...prev, numCameras: rows.length }));
                            if (fieldErrors.massiveExcel) {
                              setFieldErrors((prev) => {
                                const next = { ...prev };
                                delete next.massiveExcel;
                                return next;
                              });
                            }
                          } catch (err: any) {
                            setMassiveCameras([]);
                            setMassiveExcelName(file.name);
                            setMassiveExcelError(err?.message || 'Error al leer el Excel');
                            setFormData((prev) => ({ ...prev, numCameras: 0 }));
                          }
                          if (massiveFileRef.current) massiveFileRef.current.value = '';
                        }}
                        className="wizard-input"
                      />
                      {massiveExcelName && (
                        <p style={{ color: '#9ca3af', fontSize: '12px', margin: '8px 0 0 0' }}>
                          Archivo: {massiveExcelName}
                          {massiveCameras.length > 0 && ` — ${massiveCameras.length} cámara(s)`}
                        </p>
                      )}
                      {massiveExcelError && (
                        <span className="wizard-field-error">{massiveExcelError}</span>
                      )}
                      {fieldErrors.massiveExcel && (
                        <span className="wizard-field-error">{fieldErrors.massiveExcel}</span>
                      )}
                      {massiveCameras.length > 0 && (
                        <div style={{ marginTop: 10, fontSize: 12, color: '#d1d5db' }}>
                          <strong>Vista previa (primeras 5):</strong>
                          <ul style={{ margin: '6px 0 0 0', paddingLeft: 18 }}>
                            {massiveCameras.slice(0, 5).map((cam, i) => {
                              const rtsp = cam.rtsp.length > 64 ? `${cam.rtsp.slice(0, 64)}…` : cam.rtsp;
                              return (
                                <li key={`${i}-${cam.rtsp}`}>
                                  {cam.name || `Cámara ${i + 1}`}: {rtsp}
                                </li>
                              );
                            })}
                          </ul>
                          {massiveCameras.length > 5 && (
                            <p style={{ margin: '4px 0 0 0', color: '#9ca3af' }}>
                              … y {massiveCameras.length - 5} más
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Step 3: Devices */}
          {currentStep === 3 && (
            <div className="wizard-step-content">
              {!showDeviceSelector && (
              <div className="wizard-step-header">
                <h3 className="wizard-step-title">Asignar Dispositivos</h3>
                <p className="wizard-step-description">
                    Seleccione el tipo de dispositivo que desea agregar al sitio
                </p>
              </div>
              )}

              {!showDeviceSelector ? (
                <>
                  {/* Mensaje: cámaras automáticas solo en creación para PRIVADO/EU */}
                  {!editingSite && (formData.clientServicesId === 1 || formData.clientServicesId === 2) && (
                    <div className="wizard-info-box" style={{ marginBottom: '20px' }}>
                      <h4 className="wizard-info-title">Cámaras automáticas</h4>
                      <p className="wizard-info-text">
                        Las cámaras se crearán automáticamente al agregar un XTAM Video al sitio, según el número de cámaras (1 a 4) seleccionado en el paso 1. No es necesario asignar cámaras manualmente.
                      </p>
                    </div>
                  )}

                  {isXtamMasivos && (
                    <div className="wizard-info-box" style={{ marginBottom: '20px' }}>
                      <h4 className="wizard-info-title">Dispositivo: XTAM Server</h4>
                      <p className="wizard-info-text">
                        Se aprovisionará un XTAM Server y {massiveCameras.length || formData.numCameras || 0} cámara(s) desde el Excel.
                        No es obligatorio seleccionar dispositivos aquí; puede continuar directamente.
                      </p>
                    </div>
                  )}

                  {/* Tarjetas de selección de tipo de dispositivo */}
                  <div className="device-cards-grid">
                    {getAvailableDeviceTypes().map((deviceConfig) => (
                      <DeviceInventoryCard
                        key={deviceConfig.type}
                        title={deviceConfig.title}
                        description={deviceConfig.description}
                        icon={deviceConfig.icon}
                        selectedCount={getSelectedIdsByType(deviceConfig.type).length}
                        totalAvailable={getDevicesByType(deviceConfig.type).length}
                        maxSelection={
                          (formData.clientServicesId === 1 || formData.clientServicesId === 2 || isXtamMasivos)
                            ? (deviceConfig.type === 'cameras' ? MAX_CAMERAS : deviceConfig.type === 'xtamVideos' ? 1 : undefined)
                            : undefined
                        }
                        onClick={() => handleOpenDeviceSelector(deviceConfig.type)}
                        variant={deviceConfig.variant}
                      />
                    ))}
                  </div>

                  {/* Resumen de dispositivos seleccionados */}
                  {(formData.selectedCameras.length > 0 ||
                    formData.selectedXtamVideos.length > 0 ||
                    formData.selectedMiniots.length > 0 ||
                    formData.selectedRouters.length > 0 ||
                    formData.selectedButtons.length > 0 ||
                    formData.selectedSirens.length > 0) && (
                    <div className="selected-devices-summary">
                      <h4 className="summary-title">Dispositivos Agregados</h4>
                      
                      {/* Cámaras agregadas */}
                      {formData.selectedCameras.length > 0 && (
                        <div className="summary-section">
                          <div className="summary-section-header">
                            <Camera size={16} />
                            <h5>Cámaras ({formData.selectedCameras.length})</h5>
                          </div>
                          <div className="summary-devices-list">
                            {formData.selectedCameras.map(id => {
                              const device = getDeviceById('cameras', id);
                              if (!device) return null;
                              const serial = device.serial || device.serialDevice || device.serial_device || 'Sin serial';
                              const model = device.modelName || device.model_name || '-';
                              return (
                                <div key={id} className="summary-device-item">
                                  <div className="summary-device-info">
                                    <span className="summary-device-serial">{serial}</span>
                                    <span className="summary-device-model">{model}</span>
                                    {hasConfiguration('cameras', id) && (
                                      <span className="summary-device-configured">✓ Configurado</span>
                                    )}
                                  </div>
                                  <div className="summary-device-actions">
                                    <button
                                      type="button"
                                      onClick={() => handleConfigureDevice('cameras', id)}
                                      className={`summary-device-configure ${hasConfiguration('cameras', id) ? 'configured' : ''}`}
                                      title={hasConfiguration('cameras', id) ? 'Editar configuración' : 'Configurar dispositivo'}
                                    >
                                      <Settings size={14} />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveDevice('cameras', id)}
                                      className="summary-device-remove"
                                      title="Eliminar"
                                    >
                                      <Trash2 size={14} />
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* XTAM Videos agregados */}
                      {formData.selectedXtamVideos.length > 0 && (
                        <div className="summary-section">
                          <div className="summary-section-header">
                            <Server size={16} />
                            <h5>XTAM Video ({formData.selectedXtamVideos.length})</h5>
                          </div>
                          <div className="summary-devices-list">
                            {formData.selectedXtamVideos.map(id => {
                              const device = getDeviceById('xtamVideos', id);
                              if (!device) return null;
                              const serial = device.serial || device.serialDevice || device.serial_device || 'Sin serial';
                              const model = device.modelName || device.model_name || '-';
                              return (
                                <div key={id} className="summary-device-item">
                                  <div className="summary-device-info">
                                    <span className="summary-device-serial">{serial}</span>
                                    <span className="summary-device-model">{model}</span>
                                    {hasConfiguration('xtamVideos', id) && (
                                      <span className="summary-device-configured">✓ Configurado</span>
                                    )}
                                  </div>
                                  <div className="summary-device-actions">
                                    <button
                                      type="button"
                                      onClick={() => handleConfigureDevice('xtamVideos', id)}
                                      className={`summary-device-configure ${hasConfiguration('xtamVideos', id) ? 'configured' : ''}`}
                                      title={hasConfiguration('xtamVideos', id) ? 'Editar configuración' : 'Configurar dispositivo'}
                                    >
                                      <Settings size={14} />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveDevice('xtamVideos', id)}
                                      className="summary-device-remove"
                                      title="Eliminar"
                                    >
                                      <Trash2 size={14} />
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* MinIoT agregados */}
                      {formData.selectedMiniots.length > 0 && (
                        <div className="summary-section">
                          <div className="summary-section-header">
                            <Wifi size={16} />
                            <h5>MinIoT ({formData.selectedMiniots.length})</h5>
                          </div>
                          <div className="summary-devices-list">
                            {formData.selectedMiniots.map(id => {
                              const device = getDeviceById('miniots', id);
                              if (!device) return null;
                              const serial = device.serial || device.serialDevice || device.serial_device || 'Sin serial';
                              const model = device.modelName || device.model_name || '-';
                              return (
                                <div key={id} className="summary-device-item">
                                  <div className="summary-device-info">
                                    <span className="summary-device-serial">{serial}</span>
                                    <span className="summary-device-model">{model}</span>
                                    {hasConfiguration('miniots', id) && (
                                      <span className="summary-device-configured">✓ Configurado</span>
                                    )}
                                  </div>
                                  <div className="summary-device-actions">
                                    <button
                                      type="button"
                                      onClick={() => handleConfigureDevice('miniots', id)}
                                      className={`summary-device-configure ${hasConfiguration('miniots', id) ? 'configured' : ''}`}
                                      title={hasConfiguration('miniots', id) ? 'Editar configuración' : 'Configurar dispositivo'}
                                    >
                                      <Settings size={14} />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveDevice('miniots', id)}
                                      className="summary-device-remove"
                                      title="Eliminar"
                                    >
                                      <Trash2 size={14} />
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Routers agregados */}
                      {formData.selectedRouters.length > 0 && (
                        <div className="summary-section">
                          <div className="summary-section-header">
                            <Router size={16} />
                            <h5>Routers ({formData.selectedRouters.length})</h5>
                          </div>
                          <div className="summary-devices-list">
                            {formData.selectedRouters.map(id => {
                              const device = getDeviceById('routers', id);
                              if (!device) return null;
                              const serial = device.serial || device.serialDevice || device.serial_device || 'Sin serial';
                              const model = device.modelName || device.model_name || '-';
                              return (
                                <div key={id} className="summary-device-item">
                                  <div className="summary-device-info">
                                    <span className="summary-device-serial">{serial}</span>
                                    <span className="summary-device-model">{model}</span>
                                    {hasConfiguration('routers', id) && (
                                      <span className="summary-device-configured">✓ Configurado</span>
                                    )}
                                  </div>
                                  <div className="summary-device-actions">
                                    <button
                                      type="button"
                                      onClick={() => handleConfigureDevice('routers', id)}
                                      className={`summary-device-configure ${hasConfiguration('routers', id) ? 'configured' : ''}`}
                                      title={hasConfiguration('routers', id) ? 'Editar configuración' : 'Configurar dispositivo'}
                                    >
                                      <Settings size={14} />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveDevice('routers', id)}
                                      className="summary-device-remove"
                                      title="Eliminar"
                                    >
                                      <Trash2 size={14} />
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Botones agregados */}
                      {formData.selectedButtons.length > 0 && (
                        <div className="summary-section">
                          <div className="summary-section-header">
                            <AlertCircle size={16} />
                            <h5>Botones de Pánico ({formData.selectedButtons.length})</h5>
                          </div>
                          <div className="summary-devices-list">
                            {formData.selectedButtons.map(id => {
                              const device = getDeviceById('buttons', id);
                              if (!device) return null;
                              const serial = device.serial || device.serialDevice || device.serial_device || 'Sin serial';
                              const model = device.modelName || device.model_name || '-';
                              return (
                                <div key={id} className="summary-device-item">
                                  <div className="summary-device-info">
                                    <span className="summary-device-serial">{serial}</span>
                                    <span className="summary-device-model">{model}</span>
                                    {hasConfiguration('buttons', id) && (
                                      <span className="summary-device-configured">✓ Configurado</span>
                                    )}
                                  </div>
                                  <div className="summary-device-actions">
                                    <button
                                      type="button"
                                      onClick={() => handleConfigureDevice('buttons', id)}
                                      className={`summary-device-configure ${hasConfiguration('buttons', id) ? 'configured' : ''}`}
                                      title={hasConfiguration('buttons', id) ? 'Editar configuración' : 'Configurar dispositivo'}
                                    >
                                      <Settings size={14} />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveDevice('buttons', id)}
                                      className="summary-device-remove"
                                      title="Eliminar"
                                    >
                                      <Trash2 size={14} />
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Sirenas agregadas */}
                      {formData.selectedSirens.length > 0 && (
                        <div className="summary-section">
                          <div className="summary-section-header">
                            <Volume2 size={16} />
                            <h5>Sirenas ({formData.selectedSirens.length})</h5>
                          </div>
                          <div className="summary-devices-list">
                            {formData.selectedSirens.map(id => {
                              const device = getDeviceById('sirens', id);
                              if (!device) return null;
                              const serial = device.serial || device.serialDevice || device.serial_device || 'Sin serial';
                              const model = device.modelName || device.model_name || '-';
                              return (
                                <div key={id} className="summary-device-item">
                                  <div className="summary-device-info">
                                    <span className="summary-device-serial">{serial}</span>
                                    <span className="summary-device-model">{model}</span>
                                    {hasConfiguration('sirens', id) && (
                                      <span className="summary-device-configured">✓ Configurado</span>
                                    )}
                                  </div>
                                  <div className="summary-device-actions">
                                    <button
                                      type="button"
                                      onClick={() => handleConfigureDevice('sirens', id)}
                                      className={`summary-device-configure ${hasConfiguration('sirens', id) ? 'configured' : ''}`}
                                      title={hasConfiguration('sirens', id) ? 'Editar configuración' : 'Configurar dispositivo'}
                                    >
                                      <Settings size={14} />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveDevice('sirens', id)}
                                      className="summary-device-remove"
                                      title="Eliminar"
                                    >
                                      <Trash2 size={14} />
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <>
                  {/* Selector de dispositivos */}
                  {selectedDeviceType && (
                    <>
                      <div className="device-selector-back-bar">
                        <button
                          type="button"
                          onClick={handleCloseDeviceSelector}
                          className="device-selector-back-btn"
                        >
                          <ArrowLeft size={14} />
                          Volver
                        </button>
                      </div>
                      
                  <DeviceSelector
                        key={selectedDeviceType} // Forzar remontaje al cambiar tipo
                        devices={getDevicesByType(selectedDeviceType)}
                        selectedIds={getSelectedIdsByType(selectedDeviceType)}
                        onSelectionChange={(ids) => handleSelectionChange(selectedDeviceType, ids)}
                        title={
                          selectedDeviceType === 'cameras' ? 'Cámaras' :
                          selectedDeviceType === 'xtamVideos' ? 'XTAM Video' :
                          selectedDeviceType === 'miniots' ? 'MinIoT' :
                          selectedDeviceType === 'routers' ? 'Routers' :
                          selectedDeviceType === 'buttons' ? 'Botones de Pánico' :
                          'Sirenas'
                        }
                        icon={
                          selectedDeviceType === 'cameras' ? <Camera size={16} /> :
                          selectedDeviceType === 'xtamVideos' ? <Server size={16} /> :
                          selectedDeviceType === 'miniots' ? <Wifi size={16} /> :
                          selectedDeviceType === 'routers' ? <Router size={16} /> :
                          selectedDeviceType === 'buttons' ? <AlertCircle size={16} /> :
                          <Volume2 size={16} />
                        }
                        emptyMessage={`No hay ${
                          selectedDeviceType === 'cameras' ? 'cámaras' :
                          selectedDeviceType === 'xtamVideos' ? 'XTAM Video' :
                          selectedDeviceType === 'miniots' ? 'MinIoT' :
                          selectedDeviceType === 'routers' ? 'routers' :
                          selectedDeviceType === 'buttons' ? 'botones de pánico' :
                          'sirenas'
                        } disponibles en el inventario`}
                        maxSelection={
                          (formData.clientServicesId === 1 || formData.clientServicesId === 2)
                            ? (selectedDeviceType === 'cameras' ? MAX_CAMERAS : selectedDeviceType === 'xtamVideos' ? 1 : undefined)
                            : undefined
                        }
                    searchPlaceholder="Buscar por serial, MAC, marca o modelo..."
                        currentSiteId={editingSite?.id}
                        showCreateButton={true}
                        onCreateNew={handleCreateNewDevice}
                        onReassign={handleReassignRequest}
                        onFinish={handleCloseDeviceSelector}
                        onRefresh={() => refreshInventoryByType(selectedDeviceType!)}
                        onEditDevice={(deviceId) => handleConfigureDevice(selectedDeviceType!, deviceId)}
                  />
                    </>
                  )}
                </>
                )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="wizard-footer">
          <button
            onClick={currentStep === 1 ? handleClose : handleBack}
            disabled={loading}
            className="wizard-btn-secondary"
          >
            <ArrowLeft size={18} />
            {currentStep === 1 ? 'Cancelar' : 'Anterior'}
          </button>

          {currentStep < 3 ? (
            <button
              onClick={handleNext}
              disabled={loading}
              className="wizard-btn-primary"
            >
              Continuar
              <ArrowRight size={18} />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={loading || showDeviceSelector}
              className="wizard-btn-primary"
              title={showDeviceSelector ? 'Debe terminar la selección de dispositivos primero' : ''}
            >
              {loading 
                ? (editingSite ? 'Actualizando...' : 'Creando...') 
                : (editingSite ? 'Actualizar Sitio' : 'Crear Sitio')
              }
              <CheckCircle size={18} />
            </button>
          )}
        </div>
      </div>

      {/* Location Picker Modal */}
      <LocationPickerModal
        isOpen={showLocationPicker}
        onClose={() => setShowLocationPicker(false)}
        onSelectLocation={(lat, lng, neighborhood, location) => {
          setFormData({
            ...formData,
            latitude: lat.toFixed(6),
            longitude: lng.toFixed(6),
            neighborhood: neighborhood || formData.neighborhood,
            location: location || formData.location,
          });
        }}
        initialLat={formData.latitude ? parseFloat(formData.latitude) : undefined}
        initialLng={formData.longitude ? parseFloat(formData.longitude) : undefined}
      />

      {/* Create Device Modal (Simple) */}
      {showCreateDeviceModal && selectedDeviceType && (
        <div className="wizard-overlay" style={{ zIndex: 3000 }}>
          <div className="wizard-container" style={{ maxWidth: '600px' }}>
            <div className="wizard-header">
              <div>
                <h2 className="wizard-title">
                  Agregar {
                    selectedDeviceType === 'cameras' ? 'Cámara' :
                    selectedDeviceType === 'xtamVideos' ? 'XTAM Video' :
                    selectedDeviceType === 'miniots' ? 'MinIoT' :
                    selectedDeviceType === 'routers' ? 'Router' :
                    selectedDeviceType === 'buttons' ? 'Botón de Pánico' :
                    'Sirena'
                  }
                </h2>
                <p className="wizard-subtitle">Redirigiendo a la página de inventario...</p>
              </div>
              <button onClick={handleCloseCreateDeviceModal} className="wizard-close-btn">
                <X size={24} />
              </button>
            </div>
            
            <div className="wizard-content" style={{ padding: '40px', textAlign: 'center' }}>
              <div style={{ marginBottom: '20px' }}>
                <AlertCircle size={48} style={{ color: '#60a5fa' }} />
              </div>
              <p style={{ color: '#9ca3af', marginBottom: '24px' }}>
                Para agregar un nuevo dispositivo al inventario, debes ir a la página de gestión de inventario.
              </p>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                <button
                  onClick={handleCloseCreateDeviceModal}
                  className="wizard-btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => {
                    handleCloseCreateDeviceModal();
                    window.open('/inventory', '_blank');
                  }}
                  className="wizard-btn-primary"
                >
                  Ir a Inventario
                  <ArrowRight size={18} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reassign Device Modal */}
      {showReassignModal && deviceToReassign && (
        <div className="wizard-overlay" style={{ zIndex: 3000 }}>
          <div className="wizard-container" style={{ maxWidth: '600px' }}>
            <div className="wizard-header">
              <div>
                <h2 className="wizard-title">Reasignar Dispositivo</h2>
                <p className="wizard-subtitle">Confirma la reasignación del dispositivo</p>
              </div>
              <button onClick={handleCancelReassign} className="wizard-close-btn">
                <X size={24} />
              </button>
            </div>
            
            <div className="wizard-content" style={{ padding: '32px' }}>
              <div style={{ 
                background: 'rgba(251, 146, 60, 0.1)', 
                border: '1px solid rgba(251, 146, 60, 0.3)',
                borderRadius: '8px',
                padding: '20px',
                marginBottom: '24px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                  <AlertCircle size={24} style={{ color: '#fb923c', flexShrink: 0 }} />
                  <h3 style={{ color: '#fb923c', margin: 0, fontSize: '16px', fontWeight: 600 }}>
                    Advertencia
                  </h3>
                </div>
                <p style={{ color: '#fdba74', margin: 0, fontSize: '14px', lineHeight: '1.5' }}>
                  Este dispositivo está actualmente asignado a <strong>{deviceToReassign.fromSiteName}</strong>.
                </p>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <p style={{ color: '#e5e7eb', fontSize: '14px', lineHeight: '1.6', margin: '0 0 12px 0' }}>
                  ¿Estás seguro de que deseas reasignar este dispositivo al nuevo sitio?
                </p>
                <ul style={{ color: '#9ca3af', fontSize: '13px', lineHeight: '1.6', margin: 0, paddingLeft: '20px' }}>
                  <li>El dispositivo será removido de <strong>{deviceToReassign.fromSiteName}</strong></li>
                  <li>Será asignado a este nuevo sitio cuando lo crees</li>
                  <li>Esta acción puede afectar el monitoreo del sitio actual</li>
                </ul>
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={handleCancelReassign}
                  className="wizard-btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleConfirmReassign}
                  className="wizard-btn-primary"
                  style={{ 
                    background: 'linear-gradient(135deg, #fb923c, #f97316)',
                    borderColor: '#fb923c'
                  }}
                >
                  <CheckCircle size={18} />
                  Confirmar Reasignación
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal de configuración de dispositivo */}
      {showConfigModal && currentConfigDevice && (
        <DeviceConfigModal
          isOpen={showConfigModal}
          onClose={() => {
            if (!isConfigRequired) {
              setShowConfigModal(false);
              setCurrentConfigDevice(null);
            }
          }}
          onSave={handleSaveDeviceConfig}
          onCancel={isConfigRequired ? handleCancelDeviceConfig : undefined}
          deviceType={
            currentConfigDevice.type === 'cameras' ? 'camera' :
            currentConfigDevice.type === 'xtamVideos' ? 'xtamVideo' :
            currentConfigDevice.type === 'miniots' ? 'miniot' :
            currentConfigDevice.type === 'routers' ? 'router' :
            currentConfigDevice.type === 'buttons' ? 'button' :
            'siren'
          }
          deviceInfo={currentConfigDevice.info}
          initialConfig={deviceConfigurations[`${currentConfigDevice.type}_${currentConfigDevice.id}`]}
          isRequired={isConfigRequired}
          siteServiceId={formData.clientServicesId}
          siteLatitude={formData.latitude}
          siteLongitude={formData.longitude}
          siteSirens={siteSirenDevices}
        />
      )}
    </div>
  );
};

export default CreateSiteWizard;
